'use client';

import { Badge } from '@liveauctioneers/hammer-ui-core/badge';
import { CompanySwitcher } from './components/CompanySwitcher/CompanySwitcher';
import { CrossListingConfigPanel } from './components/CrossListingConfigPanel/CrossListingConfigPanel';
import { FeatureFlagKeys } from '@/types/featureFlags';
import { getAdminPortalUrl } from './getAdminPortalUrl';
import { SubtleInverseLink } from '@liveauctioneers/hammer-ui-core/link';
import { useAppContext } from '@/providers/AppContextProvider/AppContextProvider';
import { useEffect, useRef, useState } from 'react';
import { useEffectiveHouseId } from '@/hooks/useEffectiveHouseId/useEffectiveHouseId';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useGetHouseById } from '@/data/house/useGetHouseById';
import { useHasPermission } from '@/hooks/useHasPermission/useHasPermission';
import { useTranslations } from 'next-intl';

const TOOLBAR_VISIBLE_STORAGE_ITEM = 'atg-admin-toolbar-visible';
const ADMIN_BAR_HEIGHT_VAR = '--admin-bar-height';
const COLLAPSED_HEIGHT_PX = 4;
const CROSS_LISTING_PANEL_ID = 'cross-listing-config-panel';

type OpenOverlay = 'company' | 'crossListing' | 'none';

export const AdminToolbar = () => {
    const [isVisible, setIsVisible] = useState<boolean>(true);
    const [openOverlay, setOpenOverlay] = useState<OpenOverlay>('none');
    const { isAtgAdmin } = useHasPermission();
    const { deployment, userDecoded } = useAppContext();
    const effectiveHouseId = useEffectiveHouseId();
    const { data: house } = useGetHouseById(effectiveHouseId);
    const { data: membershipHouse } = useGetHouseById(userDecoded.houseId);
    const isCrossListingEnabled = useFeatureFlag(FeatureFlagKeys.CROSS_LISTING_BASICS);
    const t = useTranslations('AdminToolbar.crossListing');
    const crossListingRef = useRef<HTMLDivElement>(null);
    const expandedRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const stored = localStorage.getItem(TOOLBAR_VISIBLE_STORAGE_ITEM);
        if (stored !== null) {
            setIsVisible(stored !== 'false');
        }
    }, []);

    useEffect(() => {
        localStorage.setItem(TOOLBAR_VISIBLE_STORAGE_ITEM, String(isVisible));
    }, [isVisible]);

    useEffect(() => {
        if (openOverlay !== 'crossListing') {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            if (
                event.target instanceof Node &&
                crossListingRef.current &&
                !crossListingRef.current.contains(event.target)
            ) {
                setOpenOverlay('none');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [openOverlay]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.code === 'Escape') {
                setOpenOverlay('none');
                return;
            }
            if (event.altKey && event.shiftKey && event.code === 'KeyT') {
                setOpenOverlay('none');
                setIsVisible((prev) => !prev);
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);

    useEffect(() => {
        if (!isAtgAdmin) {
            document.documentElement.style.setProperty(ADMIN_BAR_HEIGHT_VAR, '0px');
            return;
        }
        const height = isVisible ? (expandedRef.current?.offsetHeight ?? 0) : COLLAPSED_HEIGHT_PX;
        document.documentElement.style.setProperty(ADMIN_BAR_HEIGHT_VAR, `${height}px`);
    }, [isAtgAdmin, isVisible]);

    if (!isAtgAdmin) {
        return null;
    }

    if (!isVisible) {
        return (
            <button
                aria-label="Show admin toolbar"
                className="bg-black h-[4px] w-full cursor-pointer border-0 p-0"
                data-testid="admin-toolbar-collapsed"
                onClick={() => setIsVisible(true)}
                title="Click or press Option+Shift+T (Mac) / Alt+Shift+T (PC) to show admin toolbar"
                type="button"
            />
        );
    }

    return (
        <div
            className="flex w-full items-center justify-between bg-background-inverse px-md py-xs"
            data-testid="admin-toolbar-expanded"
            ref={expandedRef}
        >
            <div className="flex items-center gap-sm">
                <Badge
                    color="warning"
                    emphasis="bold"
                    size="sm"
                >
                    ATG ADMIN
                </Badge>
                <SubtleInverseLink
                    href={getAdminPortalUrl(deployment)}
                    size="sm"
                >
                    Return to Admin Portal
                </SubtleInverseLink>
                <div
                    aria-hidden
                    className="h-4 w-px bg-border-primary"
                />
                <CompanySwitcher
                    houseId={effectiveHouseId}
                    houseName={house?.name}
                    isOpen={openOverlay === 'company'}
                    membershipHouseId={userDecoded.houseId}
                    membershipHouseName={membershipHouse?.name}
                    onOpenChange={(isOpen) => setOpenOverlay(isOpen ? 'company' : 'none')}
                />
                {isCrossListingEnabled && (
                    <div
                        className="relative"
                        ref={crossListingRef}
                    >
                        <button
                            aria-controls={CROSS_LISTING_PANEL_ID}
                            aria-expanded={openOverlay === 'crossListing'}
                            aria-haspopup="dialog"
                            className="cursor-pointer border-0 bg-transparent p-0 text-sm text-text-inverse"
                            data-testid="cross-listing-config-trigger"
                            onClick={() =>
                                setOpenOverlay((current) => (current === 'crossListing' ? 'none' : 'crossListing'))
                            }
                            type="button"
                        >
                            {t('trigger')}
                        </button>
                        <CrossListingConfigPanel
                            houseId={effectiveHouseId}
                            id={CROSS_LISTING_PANEL_ID}
                            isOpen={openOverlay === 'crossListing'}
                            key={effectiveHouseId}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

AdminToolbar.displayName = 'AdminToolbar';
